import { useCallback, useState } from "react";

import { inputModelToViewModelConverter, viewModelToInputModelConverter } from "@/components/features/_model/converter";

import { DEFAULT_PRODUCT_ROOM_INPUT_MODEL } from "./constants/default-input-model";
import { excludeKeys, type ExcludeKeys, type ProductRoomFormModel } from "./type";
import { roomInputZodSchema, type RoomInputZodSchemaType } from "./validation-schema";

import type { ProductRoomFormModelContextType } from "../ui/_shared/context/context";

type Props = {
	inputModel: ProductRoomFormModel["InputModel"];
	queryData: ProductRoomFormModel["QueryData"];
};

export const useInitProductRoomFormModelContext = ({
	inputModel: _inputModel,
	queryData,
}: Props): Omit<ProductRoomFormModelContextType, "queryData"> & { onResetViewModel: () => void } => {
	const hotelID = _inputModel.hotelID;
	const roomID = _inputModel.roomID;

	const defaultInputModel = DEFAULT_PRODUCT_ROOM_INPUT_MODEL({ roomID, hotelID, queryData });
	const inputModel = _inputModel ?? defaultInputModel;

	const [viewModel, setViewModel] = useState(
		inputModelToViewModelConverter<
			ProductRoomFormModel["InputModel"],
			ExcludeKeys,
			ProductRoomFormModel["QueryData"],
			RoomInputZodSchemaType
		>({
			defaultInput: defaultInputModel,
			initInput: inputModel,
			currentInput: inputModel,
			excludeKeys: excludeKeys,
			zodSchema: roomInputZodSchema,
		}),
	);

	const onSetViewModel: ProductRoomFormModelContextType["onSetViewModel"] = useCallback(
		(values) => {
			const mergedViewModel = {
				...viewModel,
				...values.reduce(
					(acc, cur) => {
						acc[cur.key] = cur.value;
						return acc;
					},
					{} as ProductRoomFormModel["ViewModel"],
				),
			} satisfies ProductRoomFormModel["ViewModel"];

			const currentInput = viewModelToInputModelConverter<
				ProductRoomFormModel["InputModel"],
				ExcludeKeys,
				ProductRoomFormModel["QueryData"]
			>(mergedViewModel);

			const newViewModel = inputModelToViewModelConverter<
				ProductRoomFormModel["InputModel"],
				ExcludeKeys,
				ProductRoomFormModel["QueryData"],
				RoomInputZodSchemaType
			>({
				defaultInput: defaultInputModel,
				initInput: inputModel,
				currentInput,
				excludeKeys: excludeKeys,
				zodSchema: roomInputZodSchema,
			});

			setViewModel(newViewModel);
		},
		[defaultInputModel, inputModel, viewModel],
	);

	const onResetViewModel = useCallback(() => {
		setViewModel({ ...viewModel, isDirty: false });
	}, [viewModel]);

	return {
		viewModel,
		onSetViewModel,

		onResetViewModel,
	};
};
